import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

// ── types (mirror backend/app/features/major_swag/schemas.py) ──────────────

export type RequestStatus = "pending" | "approved" | "denied" | "cancelled";
export type ShipmentStatus =
  | "pending"
  | "label_created"
  | "in_transit"
  | "delivered"
  | "exception"
  | "unknown";
export type RecipientKind = "person" | "group";
export type Carrier = "fedex" | "usps" | "ups" | "dhl" | "other";
export type DisbursementSource = "imported" | "request" | "direct";

export const CARRIER_LABEL: Record<Carrier, string> = {
  fedex: "FedEx",
  usps: "USPS",
  ups: "UPS",
  dhl: "DHL",
  other: "Other",
};

export const SHIPMENT_LABEL: Record<ShipmentStatus, string> = {
  pending: "Not shipped",
  label_created: "Label created",
  in_transit: "In transit",
  delivered: "Delivered",
  exception: "Problem",
  unknown: "Unknown",
};

export interface Department {
  id: string;
  name: string;
  qb_class_name: string | null;
  sort_order: number;
  is_active: boolean;
}

export interface Item {
  id: string;
  name: string;
  description: string | null;
  photo_url: string | null;
  default_unit_cost: number | null;
  low_stock_threshold: number;
  is_active: boolean;
  sort_order: number;
}

export interface DepartmentReservation {
  department_id: string;
  department_name: string;
  quantity: number;
}

export interface ItemStock {
  item_id: string;
  item_name: string;
  photo_url: string | null;
  purchased: number;
  disbursed: number;
  on_hand: number;
  reserved: number;
  pending: number;
  free: number;
  unit_cost: number | null;
  value_on_hand: number;
  low_stock: boolean;
  reservations: DepartmentReservation[];
}

export interface ShipmentTracking {
  id: string;
  carrier: Carrier | null;
  carrier_service: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  ship_to_name: string | null;
  ship_to_address: string | null;
  ship_to_phone: string | null;
  shipping_cost: number | null;
  shipped_at: string | null;
  status: ShipmentStatus;
  status_detail: string | null;
  delivered_at: string | null;
  last_checked_at: string | null;
  check_error: string | null;
  contents: string[];
  recipients: string[];
  days_in_transit: number | null;
}

export interface Shipment extends ShipmentTracking {
  notes: string | null;
  disbursement_ids: string[];
}

export interface TrackingEvent {
  occurred_at: string | null;
  status: ShipmentStatus;
  description: string | null;
  location: string | null;
  source: string;
}

export interface ShipmentDetail extends Shipment {
  events: TrackingEvent[];
}

export interface SummaryOut {
  stock: ItemStock[];
  shipments: ShipmentTracking[];
  shipment_window_days: number;
  pending_requests: number;
  total_value_on_hand: number;
  tracking_manual_only: boolean;
  tracking_providers: string[];
}

export interface PriorGift {
  item_name: string;
  use_date: string | null;
  recipient_name: string | null;
  employee_name: string | null;
  occasion: string | null;
}

export interface DuplicateCheck {
  person_matches: PriorGift[];
  company_matches: PriorGift[];
  same_item_same_person: boolean;
}

export interface RequestLine {
  id: string;
  item_id: string;
  item_name: string | null;
  quantity: number;
  recipient_kind: RecipientKind;
  recipient_name: string | null;
  recipient_email: string | null;
  recipient_company_id: string | null;
  recipient_company_name: string | null;
  occasion: string | null;
  ship_to_name: string | null;
  ship_to_address: string | null;
  ship_to_phone: string | null;
  notes: string | null;
}

export interface RequestLineIn {
  item_id: string;
  quantity: number;
  recipient_kind: RecipientKind;
  recipient_name?: string | null;
  recipient_email?: string | null;
  recipient_company_id?: string | null;
  recipient_company_name?: string | null;
  occasion?: string | null;
  ship_to_name?: string | null;
  ship_to_address?: string | null;
  ship_to_phone?: string | null;
  notes?: string | null;
}

export interface SwagRequest {
  id: string;
  requester_id: string | null;
  requester_name: string;
  department_id: string | null;
  department_name: string | null;
  purpose: string | null;
  needs_shipping: boolean;
  needed_by: string | null;
  status: RequestStatus;
  submitted_at: string | null;
  decided_by_name: string | null;
  decided_at: string | null;
  decision_note: string | null;
  lines: RequestLine[];
  total_units: number;
  can_cancel: boolean;
}

export interface RequestIn {
  department_id?: string | null;
  purpose?: string | null;
  needs_shipping: boolean;
  needed_by?: string | null;
  lines: RequestLineIn[];
}

export interface Allocation {
  id: string;
  purchase_id: string;
  department_id: string;
  department_name: string | null;
  quantity: number;
  note: string | null;
}

export interface Purchase {
  id: string;
  item_id: string;
  item_name: string | null;
  purchase_date: string | null;
  purchase_year: number | null;
  quantity: number;
  unit_cost: number;
  freight_cost: number | null;
  total_cost: number;
  vendor: string | null;
  order_reference: string | null;
  photo_url: string | null;
  notes: string | null;
  source: string;
  disbursed: number;
  on_hand: number;
  reserved: number;
  allocations: Allocation[];
}

export interface PurchaseIn {
  item_id: string;
  purchase_date?: string | null;
  purchase_year?: number | null;
  quantity: number;
  unit_cost: number;
  freight_cost?: number | null;
  vendor?: string | null;
  order_reference?: string | null;
  photo_url?: string | null;
  notes?: string | null;
}

export interface Disbursement {
  id: string;
  item_id: string;
  item_name: string;
  purchase_id: string | null;
  request_id: string | null;
  shipment_id: string | null;
  department_id: string | null;
  department_name: string | null;
  quantity: number;
  unit_cost: number;
  swag_cost: number;
  shipping_cost_share: number;
  use_date: string | null;
  employee_name: string | null;
  recipient_kind: RecipientKind;
  recipient_name: string | null;
  recipient_company_id: string | null;
  recipient_company_name: string | null;
  occasion: string | null;
  notes: string | null;
  source: DisbursementSource;
  needs_review: boolean;
  review_note: string | null;
  carrier: Carrier | null;
  carrier_service: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  shipment_status: ShipmentStatus | null;
  shipped_at: string | null;
  delivered_at: string | null;
  ship_to_address: string | null;
  /** Where the requester asked it to go — present before a box exists. */
  requested_ship_to_name: string | null;
  requested_ship_to_address: string | null;
  requested_ship_to_phone: string | null;
  /** Requester asked for it to be mailed — this, not "no shipment yet",
   *  is what puts a row in the send queue. */
  needs_shipping: boolean;
}

export interface LedgerOut {
  rows: Disbursement[];
  count: number;
  total_units: number;
  total_swag_cost: number;
  total_shipping_cost: number;
}

export interface LedgerFilters {
  item_id?: string | null;
  item_ids?: string[];
  department_id?: string | null;
  department_ids?: string[];
  include_unassigned?: boolean | null;
  /** Shipment states, plus "none" for handed over in person. */
  shipment_states?: string[];
  company?: string | null;
  q?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  needs_review?: boolean | null;
  /** Column to sort by; the server rejects anything it can't sort. */
  sort?: string | null;
  direction?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export interface AccountingRow {
  department_id: string | null;
  department_name: string;
  qb_class_name: string | null;
  units: number;
  swag_cost: number;
  shipping_cost: number;
  total_cost: number;
  items: string[];
}

export interface AccountingMonth {
  month: string;
  rows: AccountingRow[];
  units: number;
  swag_cost: number;
  shipping_cost: number;
  total_cost: number;
  unassigned_units: number;
  unassigned_cost: number;
}

export interface AccountingOut {
  months: AccountingMonth[];
  from_month: string;
  to_month: string;
  swag_cost: number;
  shipping_cost: number;
  total_cost: number;
}

export interface CompanyOption {
  id: string;
  name: string;
  is_active: boolean | null;
  account_name: string | null;
}

export interface ImportResult {
  imported: boolean;
  reason?: string;
  rows_read?: number;
  items?: number;
  lots_created: number;
  lots_existing: number;
  disbursements: number;
  shipments: number;
  units_on_hand?: number;
  needs_review?: number;
  review_sample?: { item: string; given_to: string | null; company: string | null; reasons: string[] }[];
}

// ── queries ────────────────────────────────────────────────────────────────

const KEY = "major-swag";

/** Object -> query string, dropping empty values. Takes a plain `object` so
 *  typed filter interfaces pass without an index signature. */
function qs(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      // Repeated keys (?item_ids=a&item_ids=b) — FastAPI reads a list from
      // those. `set(key, String(array))` would send "a,b" as ONE value, which
      // parses as a single malformed id.
      for (const entry of value) search.append(key, String(entry));
      continue;
    }
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export function useSummary() {
  return useQuery({
    queryKey: [KEY, "summary"],
    queryFn: () => apiFetch<SummaryOut>("/major-swag/summary"),
    staleTime: 30_000,
  });
}

export function useLedger(filters: LedgerFilters = {}) {
  return useQuery({
    queryKey: [KEY, "ledger", filters],
    queryFn: () => apiFetch<LedgerOut>(`/major-swag/ledger${qs(filters)}`),
    staleTime: 30_000,
  });
}

export function useItems() {
  return useQuery({
    queryKey: [KEY, "items"],
    queryFn: () => apiFetch<Item[]>("/major-swag/items"),
    staleTime: 300_000,
  });
}

export function useDepartments() {
  return useQuery({
    queryKey: [KEY, "departments"],
    queryFn: () => apiFetch<Department[]>("/major-swag/departments"),
    staleTime: 300_000,
  });
}

export function useRequests(status?: RequestStatus) {
  return useQuery({
    queryKey: [KEY, "requests", status ?? "all"],
    queryFn: () => apiFetch<SwagRequest[]>(`/major-swag/requests${qs({ status })}`),
    staleTime: 15_000,
  });
}

export function usePurchases(enabled = true) {
  return useQuery({
    queryKey: [KEY, "purchases"],
    queryFn: () => apiFetch<Purchase[]>("/major-swag/purchases"),
    enabled,
    staleTime: 30_000,
  });
}

export function useShipments(enabled = true) {
  return useQuery({
    queryKey: [KEY, "shipments"],
    queryFn: () => apiFetch<Shipment[]>("/major-swag/shipments"),
    enabled,
    staleTime: 30_000,
  });
}

export function useAccounting(
  range: { from_month?: string; to_month?: string },
  enabled = true,
) {
  return useQuery({
    queryKey: [KEY, "accounting", range],
    queryFn: () => apiFetch<AccountingOut>(`/major-swag/accounting${qs(range)}`),
    enabled,
    staleTime: 60_000,
  });
}

export interface ShipToSuggestion {
  ship_to_name: string | null;
  ship_to_address: string;
  ship_to_phone: string | null;
  /** "person" — we posted to them before; "company" — to a colleague. */
  matched_on: "person" | "company";
  last_recipient: string | null;
  last_used: string | null;
  times_used: number;
}

/** Addresses a box has actually reached before, for this person or company. */
export function useShipToSuggestions(
  params: { recipient_name?: string; company_id?: string; company_name?: string },
  enabled: boolean,
) {
  return useQuery({
    queryKey: [KEY, "ship-to", params],
    queryFn: () =>
      apiFetch<{ suggestions: ShipToSuggestion[] }>(
        `/major-swag/ship-to-suggestions${qs(params)}`,
      ),
    enabled:
      enabled &&
      Boolean(params.recipient_name || params.company_name || params.company_id),
    staleTime: 60_000,
  });
}

/** Runs as the requester types a recipient — debounce at the call site. */
export function useDuplicateCheck(
  params: { recipient_name?: string; company_id?: string; company_name?: string; item_id?: string },
  enabled: boolean,
) {
  return useQuery({
    queryKey: [KEY, "duplicate", params],
    queryFn: () => apiFetch<DuplicateCheck>(`/major-swag/duplicate-check${qs(params)}`),
    enabled: enabled && Boolean(params.recipient_name || params.company_name || params.company_id),
    staleTime: 60_000,
  });
}

export function useCompanySearch(q: string) {
  return useQuery({
    queryKey: [KEY, "companies", q],
    queryFn: () => apiFetch<{ items: CompanyOption[] }>(`/major-swag/companies/search${qs({ q })}`),
    enabled: q.trim().length >= 2,
    staleTime: 120_000,
  });
}

// ── mutations ──────────────────────────────────────────────────────────────

/** Anything that moves stock invalidates summary + ledger + purchases at once —
 *  they are three views of the same arithmetic. */
function useSwagMutation<V, R>(fn: (vars: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useCreateRequest() {
  return useSwagMutation((body: RequestIn) =>
    apiFetch<SwagRequest>("/major-swag/requests", { method: "POST", body }),
  );
}

export function useCancelRequest() {
  return useSwagMutation((id: string) =>
    apiFetch<SwagRequest>(`/major-swag/requests/${id}/cancel`, { method: "POST" }),
  );
}

export function useDecideRequest() {
  return useSwagMutation(
    (vars: { id: string; approve: boolean; note?: string; department_id?: string | null }) =>
      apiFetch<SwagRequest>(
        `/major-swag/requests/${vars.id}/${vars.approve ? "approve" : "deny"}`,
        { method: "POST", body: { note: vars.note, department_id: vars.department_id } },
      ),
  );
}

export function useSaveItem() {
  return useSwagMutation((vars: { id?: string; body: Omit<Item, "id"> }) =>
    vars.id
      ? apiFetch<Item>(`/major-swag/items/${vars.id}`, { method: "PATCH", body: vars.body })
      : apiFetch<Item>("/major-swag/items", { method: "POST", body: vars.body }),
  );
}

export function useDeleteItem() {
  return useSwagMutation((id: string) =>
    apiFetch<void>(`/major-swag/items/${id}`, { method: "DELETE" }),
  );
}

export function useSavePurchase() {
  return useSwagMutation((vars: { id?: string; body: Partial<PurchaseIn> }) =>
    vars.id
      ? apiFetch<Purchase>(`/major-swag/purchases/${vars.id}`, {
          method: "PATCH",
          body: vars.body,
        })
      : apiFetch<Purchase>("/major-swag/purchases", { method: "POST", body: vars.body }),
  );
}

export function useDeletePurchase() {
  return useSwagMutation((id: string) =>
    apiFetch<void>(`/major-swag/purchases/${id}`, { method: "DELETE" }),
  );
}

export function useSetAllocation() {
  return useSwagMutation(
    (vars: { purchaseId: string; department_id: string; quantity: number; note?: string }) =>
      apiFetch<Allocation>(`/major-swag/purchases/${vars.purchaseId}/allocations`, {
        method: "PUT",
        body: { department_id: vars.department_id, quantity: vars.quantity, note: vars.note },
      }),
  );
}

export function useDeleteAllocation() {
  return useSwagMutation((id: string) =>
    apiFetch<void>(`/major-swag/allocations/${id}`, { method: "DELETE" }),
  );
}

export function useCreateDisbursement() {
  return useSwagMutation((body: Record<string, unknown>) =>
    apiFetch<Disbursement>("/major-swag/disbursements", { method: "POST", body }),
  );
}

export function useUpdateDisbursement() {
  return useSwagMutation((vars: { id: string; body: Record<string, unknown> }) =>
    apiFetch<Disbursement>(`/major-swag/disbursements/${vars.id}`, {
      method: "PATCH",
      body: vars.body,
    }),
  );
}

export function useDeleteDisbursement() {
  return useSwagMutation((id: string) =>
    apiFetch<void>(`/major-swag/disbursements/${id}`, { method: "DELETE" }),
  );
}

export function useCreateShipment() {
  return useSwagMutation((body: Record<string, unknown>) =>
    apiFetch<ShipmentDetail>("/major-swag/shipments", { method: "POST", body }),
  );
}

export function useUpdateShipment() {
  return useSwagMutation((vars: { id: string; body: Record<string, unknown> }) =>
    apiFetch<ShipmentDetail>(`/major-swag/shipments/${vars.id}`, {
      method: "PATCH",
      body: vars.body,
    }),
  );
}

export function useRefreshShipment() {
  return useSwagMutation((id: string) =>
    apiFetch<ShipmentDetail>(`/major-swag/shipments/${id}/refresh`, { method: "POST" }),
  );
}

export function useRefreshAllShipments() {
  return useSwagMutation((_vars: void) =>
    apiFetch<{ checked: number; changed: number; skipped?: string }>(
      "/major-swag/shipments/refresh-all",
      { method: "POST" },
    ),
  );
}

export function useUploadPhoto() {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return apiFetch<{ url: string; filename: string }>("/major-swag/uploads", {
        method: "POST",
        body: form,
      });
    },
  });
}

export function useImportWorkbook() {
  return useSwagMutation((file: File) => {
    const form = new FormData();
    form.append("file", file);
    return apiFetch<ImportResult>("/major-swag/import-workbook", {
      method: "POST",
      body: form,
    });
  });
}

export function accountingCsvUrl(range: { from_month?: string; to_month?: string }): string {
  return `/api/major-swag/accounting.csv${qs(range)}`;
}

// ── formatting helpers ─────────────────────────────────────────────────────

/** Money to exactly 2 decimals — these figures get posted to the books, so no
 *  $K compaction. */
export function fmtMoney(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return v < 0 ? `−$${abs}` : `$${abs}`;
}

/** Whole dollars, for tiles where cents are noise. */
export function fmtMoney0(n: number | null | undefined): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `$${Math.round(Number(n)).toLocaleString("en-US")}`;
}

/** "2026-02-18" → "Feb 18, 2026". Date-only strings are rendered without
 *  constructing a Date, so a UTC-midnight value can't slip to the day before. */
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  const iso = value.slice(0, 10);
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return value;
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

/** "2026-02" → "Feb 2026". */
export function monthLabel(ym: string | null | undefined): string {
  if (!ym || ym.length < 7) return "—";
  const name = MONTHS[Number(ym.slice(5, 7)) - 1];
  return name ? `${name} ${ym.slice(0, 4)}` : ym;
}

export function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** N months back from now, as "YYYY-MM". */
export function monthsAgo(n: number): string {
  const now = new Date();
  now.setMonth(now.getMonth() - n);
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}
